import React, { useState } from 'react';
import { toast } from 'react-toastify';

const DNSInstructionsPanel = ({ domain_dns_records, domain_status, onVerify, isVerifying }) => {
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [expandedRegistrar, setExpandedRegistrar] = useState(null);

  // Only render when status is dns_updated or failed
  if (domain_status !== 'dns_updated' && domain_status !== 'failed') {
    return null;
  }

  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedIndex(index);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopiedIndex(null), 2000);
    }).catch(() => {
      toast.error('Failed to copy');
    });
  };

  const toggleRegistrar = (registrar) => {
    setExpandedRegistrar(expandedRegistrar === registrar ? null : registrar);
  };

  const registrarInstructions = {
    godaddy: {
      name: 'GoDaddy',
      steps: [
        'Log in to your GoDaddy account',
        'Go to "My Products" and find your domain',
        'Click "DNS" or "Manage DNS"',
        'Scroll to the DNS Records section',
        'Click "Add" to add each record from the table above',
        'For CNAME records: Enter the Name and Value as shown',
        'For A records: Enter @ as Name and the IP address as Value',
        'For TXT records: Enter the Name and Value as shown',
        'Save all changes and wait for DNS propagation'
      ]
    },
    namecheap: {
      name: 'Namecheap',
      steps: [
        'Log in to your Namecheap account',
        'Go to "Domain List" and select your domain',
        'Click "Manage" next to your domain',
        'Go to the "Advanced DNS" tab',
        'Click "Add New Record"',
        'Select the record type (A, CNAME, or TXT)',
        'Enter the Host and Value as shown in the table above',
        'Set TTL to Automatic or 300 seconds',
        'Click the checkmark to save',
        'Repeat for all records'
      ]
    },
    cloudflare: {
      name: 'Cloudflare',
      steps: [
        'Log in to your Cloudflare account',
        'Select your domain from the dashboard',
        'Click on the "DNS" tab',
        'Click "Add record"',
        'Select the record type (A, CNAME, or TXT)',
        'Enter the Name and Content as shown in the table',
        'Set Proxy status to "DNS only" (gray cloud)',
        'Click "Save"',
        'Repeat for all records'
      ]
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">
          <i className="fa-solid fa-network-wired mr-2"></i>
          DNS Configuration Instructions
        </h3>
      </div>
      <div className="card-body">
        {/* Status Message */}
        {domain_status === 'failed' && (
          <div className="alert alert-warning">
            <i className="fa-solid fa-exclamation-triangle mr-2"></i>
            <strong>DNS Verification Failed</strong>
            <p className="mb-0 mt-2">
              The DNS records for your domain don't match the required configuration. 
              Please verify that you've added all records correctly and wait for DNS propagation (24-48 hours).
            </p>
          </div>
        )}

        {domain_status === 'dns_updated' && (
          <div className="alert alert-info">
            <i className="fa-solid fa-info-circle mr-2"></i>
            <strong>DNS Configuration Required</strong>
            <p className="mb-0 mt-2">
              Please add the following DNS records to your domain registrar. 
              After adding the records, it may take 24-48 hours for DNS changes to propagate globally.
            </p>
          </div>
        )}

        {/* DNS Records Table */}
        <div className="table-responsive mt-3">
          <table className="table table-bordered table-striped">
            <thead className="thead-dark">
              <tr>
                <th>Type</th>
                <th>Name</th>
                <th>Value</th>
                <th>TTL</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {domain_dns_records && domain_dns_records.length > 0 ? (
                domain_dns_records.map((record, index) => (
                  <tr key={index}>
                    <td>
                      <span className="badge badge-primary">{record.type}</span>
                    </td>
                    <td><code>{record.name}</code></td>
                    <td>
                      <code style={{ wordBreak: 'break-all' }}>{record.value}</code>
                    </td>
                    <td>{record.ttl || 300}</td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => copyToClipboard(record.value, index)}
                      >
                        {copiedIndex === index ? (
                          <>
                            <i className="fa-solid fa-check mr-1"></i>
                            Copied
                          </>
                        ) : (
                          <>
                            <i className="fa-solid fa-copy mr-1"></i>
                            Copy
                          </>
                        )}
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center text-muted">
                    No DNS records available. Please contact support.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* DNS Propagation Info */}
        <div className="alert alert-light mt-3">
          <h5>
            <i className="fa-solid fa-clock mr-2"></i>
            DNS Propagation Time
          </h5>
          <p className="mb-0">
            DNS changes typically take <strong>24-48 hours</strong> to propagate globally. 
            You can verify DNS propagation using online tools like{' '}
            <a href="https://www.whatsmydns.net/" target="_blank" rel="noopener noreferrer">
              WhatsMyDNS.net
            </a>
          </p>
        </div>

        {/* Registrar-Specific Instructions */}
        <div className="mt-4">
          <h5>Step-by-Step Instructions for Common Registrars</h5>
          <p className="text-muted">Click on your domain registrar to see detailed instructions:</p>
          
          <div className="accordion" id="registrarAccordion">
            {Object.entries(registrarInstructions).map(([key, registrar]) => (
              <div className="card" key={key}>
                <div className="card-header" id={`heading-${key}`}>
                  <h2 className="mb-0">
                    <button
                      className={`btn btn-link btn-block text-left ${expandedRegistrar === key ? '' : 'collapsed'}`}
                      type="button"
                      onClick={() => toggleRegistrar(key)}
                    >
                      <i className={`fa-solid ${expandedRegistrar === key ? 'fa-chevron-down' : 'fa-chevron-right'} mr-2`}></i>
                      {registrar.name}
                    </button>
                  </h2>
                </div>
                <div
                  className={`collapse ${expandedRegistrar === key ? 'show' : ''}`}
                  id={`collapse-${key}`}
                >
                  <div className="card-body">
                    <ol>
                      {registrar.steps.map((step, index) => (
                        <li key={index} className="mb-2">{step}</li>
                      ))}
                    </ol>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Verify DNS Button */}
        <div className="mt-4 text-center">
          <button
            className="btn btn-primary btn-lg"
            onClick={onVerify}
            disabled={isVerifying}
          >
            {isVerifying ? (
              <>
                <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                Verifying DNS Configuration...
              </>
            ) : (
              <>
                <i className="fa-solid fa-check-circle mr-2"></i>
                Verify DNS Configuration
              </>
            )}
          </button>
          <p className="text-muted mt-2 mb-0">
            Click this button after you've added the DNS records to verify your configuration
          </p>
        </div>
      </div>
    </div>
  );
};

export default DNSInstructionsPanel;
